import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { coversDate, hasStayDayInRange, type ScheduleReservation, type StayNature, type StayStatus } from "./scheduleModel";

export const stayScheduleKey = (clinicId: string | null, from: string, to: string) =>
  ["stay-schedule", clinicId, from, to];

/**
 * Só consulta com um intervalo real. Um chamador sem nada para buscar passa
 * `""`, e o Postgres recusa string vazia como data (22007) — o erro estourava
 * na tela em vez de simplesmente não haver consulta.
 */
export const stayScheduleEnabled = (clinicId: string | null, from: string, to: string) =>
  Boolean(clinicId) && from !== "" && to !== "";

type ReservationRow = {
  id: string;
  pet_id: string;
  module_type: string;
  check_in_date: string;
  check_out_date: string | null;
  check_in_time: string | null;
  arrival_shift?: string | null;
  status: string;
  is_overnight: boolean;
  pets: { name: string; birth_date: string | null; owners: { id: string; name: string } | null } | null;
  hotel_room_beds: { name: string; hotel_rooms: { name: string } | null } | null;
  stay_reservation_daycare_plan_usages: Array<{ id: string; contract_id: string }> | null;
  stay_reservation_hotel_plan_usages: Array<{ id: string; contract_id: string }> | null;
};

/** Contrato tem pelo menos uma venda paga e nenhuma charge vencida em aberto. */
function isContractPaid(charges: Array<{ due_on: string; sale: { status: string } | null }>, today: string) {
  const hasPaidCharge = charges.some((charge) => charge.sale?.status === "PAGA");
  const hasOpenOverdueCharge = charges.some((charge) => charge.due_on <= today && charge.sale?.status !== "PAGA");
  return hasPaidCharge && !hasOpenOverdueCharge;
}

const bedLabelOf = (row: ReservationRow) => {
  const bed = row.hotel_room_beds;
  if (!bed) return null;
  return bed.hotel_rooms?.name ? `${bed.hotel_rooms.name} · ${bed.name}` : bed.name;
};

const toReservation = (
  row: ReservationRow,
  paidByContractId: Map<string, boolean>,
): ScheduleReservation => {
  const usages = [...(row.stay_reservation_daycare_plan_usages ?? []), ...(row.stay_reservation_hotel_plan_usages ?? [])];
  const covered = usages.length > 0;
  const paid = covered && usages.every((usage) => paidByContractId.get(usage.contract_id) === true);

  return {
    id: row.id,
    petId: row.pet_id,
    petName: row.pets?.name ?? "",
    ownerId: row.pets?.owners?.id ?? null,
    ownerName: row.pets?.owners?.name ?? "",
    nature: (row.module_type === "hotel" || row.module_type === "both" ? row.module_type : "daycare") as StayNature,
    checkInDate: row.check_in_date,
    checkOutDate: row.check_out_date,
    checkInTime: row.check_in_time,
    arrivalShift: row.arrival_shift === "morning" || row.arrival_shift === "evening" ? row.arrival_shift : null,
    status: row.status as StayStatus,
    isOvernight: row.is_overnight,
    bedLabel: bedLabelOf(row),
    birthDate: row.pets?.birth_date ?? null,
    stayPlan: covered ? { covered, paid } : null,
  };
};

/**
 * Every reservation touching the visible range. The range covers a whole month
 * or a whole week, so one query feeds the grid *and* the day list below it.
 */
export function useStaySchedule({ from, to }: { from: string; to: string }) {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: stayScheduleKey(clinicId, from, to),
    enabled: stayScheduleEnabled(clinicId, from, to),
    queryFn: async (): Promise<ScheduleReservation[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from("stay_reservations")
        .select(
          `id, pet_id, module_type, check_in_date, check_out_date, check_in_time, arrival_shift, status, is_overnight,
           pets ( name, birth_date, owners ( id, name ) ),
           hotel_room_beds ( name, hotel_rooms ( name ) ),
           stay_reservation_daycare_plan_usages ( id, contract_id ),
           stay_reservation_hotel_plan_usages ( id, contract_id )`,
        )
        .eq("clinic_id", clinicId)
        .eq("status", "reserved")
        .lte("check_in_date", to)
        .or(`check_out_date.is.null,check_out_date.gte.${from}`)
        .order("check_in_time", { nullsFirst: false });
      if (error) throw error;

      const reservationIds = (data ?? []).map((row) => row.id);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: dayRows, error: dayError } = reservationIds.length > 0
        ? await (supabase as any)
            .from("stay_reservation_days")
            .select("id, stay_reservation_id, stay_date, status")
            .in("stay_reservation_id", reservationIds)
            .gte("stay_date", from)
            .lte("stay_date", to)
        : { data: [] as Array<{ id: string; stay_reservation_id: string; stay_date: string; status: string }>, error: null };
      if (dayError) throw dayError;

      const dayStatusByReservation = new Map<string, Record<string, ScheduleReservation["status"]>>();
      const dayIdByReservation = new Map<string, Record<string, string>>();
      for (const row of (dayRows ?? []) as Array<{ id: string; stay_reservation_id: string; stay_date: string; status: string }>) {
        const displayStatus = row.status === "scheduled" ? "reserved" : (row.status as ScheduleReservation["status"]);
        const statusByDate = dayStatusByReservation.get(row.stay_reservation_id) ?? {};
        statusByDate[row.stay_date] = displayStatus;
        dayStatusByReservation.set(row.stay_reservation_id, statusByDate);

        const idByDate = dayIdByReservation.get(row.stay_reservation_id) ?? {};
        idByDate[row.stay_date] = row.id;
        dayIdByReservation.set(row.stay_reservation_id, idByDate);
      }

      const rows = (data as unknown as ReservationRow[]) ?? [];
      const daycareContractIds = [...new Set(rows.flatMap((row) => (row.stay_reservation_daycare_plan_usages ?? []).map((usage) => usage.contract_id)))];
      const hotelContractIds = [...new Set(rows.flatMap((row) => (row.stay_reservation_hotel_plan_usages ?? []).map((usage) => usage.contract_id)))];

      const today = new Date().toLocaleDateString("en-CA");
      const [daycareCharges, hotelCharges] = await Promise.all([
        daycareContractIds.length > 0
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? (supabase as any).from("daycare_plan_charges").select("contract_id, due_on, sale:sales(status)").in("contract_id", daycareContractIds)
          : { data: [] as Array<{ contract_id: string; due_on: string; sale: { status: string } | null }>, error: null },
        hotelContractIds.length > 0
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? (supabase as any).from("hotel_plan_charges").select("contract_id, due_on, sale:sales(status)").in("contract_id", hotelContractIds)
          : { data: [] as Array<{ contract_id: string; due_on: string; sale: { status: string } | null }>, error: null },
      ]);
      if (daycareCharges.error) throw daycareCharges.error;
      if (hotelCharges.error) throw hotelCharges.error;

      const chargesByContract = new Map<string, Array<{ due_on: string; sale: { status: string } | null }>>();
      for (const charge of [...(daycareCharges.data ?? []), ...(hotelCharges.data ?? [])] as Array<{ contract_id: string; due_on: string; sale: { status: string } | null }>) {
        chargesByContract.set(charge.contract_id, [...(chargesByContract.get(charge.contract_id) ?? []), charge]);
      }
      const paidByContractId = new Map<string, boolean>();
      for (const [contractId, charges] of chargesByContract) {
        paidByContractId.set(contractId, isContractPaid(charges, today));
      }

      // An open non-recurring stay only occupies its check-in day, so the
      // `check_out_date is null` branch above can let older rows through. A
      // recurring stay is instead kept when it has materialized days inside
      // the visible range.
      return rows
        .map((row) => ({
          ...toReservation(row, paidByContractId),
          dayStatusByDate: dayStatusByReservation.get(row.id),
          dayIdByDate: dayIdByReservation.get(row.id),
        }))
        .filter((reservation) =>
          coversDate(reservation, from) ||
          reservation.checkInDate >= from ||
          hasStayDayInRange(reservation, from, to),
        );
    },
  });
}
